import axios from 'axios';
import { extractInternalFileKey } from './fileUrlService';

/** Google OAuth callback is registered only on workloop.chat, so start/status must hit that API. */
const GOOGLE_API_URL = 'https://workloop.chat/api';

const googleApi = () => {
  if (typeof window !== 'undefined' && /workloop\.chat$/i.test(window.location.hostname)) {
    return `${window.location.origin}/api`;
  }
  return GOOGLE_API_URL;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: token ? `Bearer ${token}` : '' };
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export const getGooglePluginStatus = async () => {
  const response = await axios.get(`${googleApi()}/google/oauth/status`, { headers: authHeaders() });
  const data = response.data || {};
  return {
    oauthLoginAvailable: data.oauthLoginAvailable === true,
    calendarConnected: data.calendarConnected === true,
    driveConnected: data.driveConnected === true,
    email: data.email || '',
    displayName: data.displayName || '',
    calendarLastSyncedAt: data.calendarLastSyncedAt || null,
  };
};

export const startGoogleOauth = async (plugin) => {
  try {
    const response = await axios.post(
      `${googleApi()}/google/oauth/start`,
      {},
      { headers: authHeaders(), params: { plugin }, withCredentials: true }
    );
    return response.data?.authorizationUrl;
  } catch (error) {
    throw new Error(errorMessage(error, 'Could not start Google login'));
  }
};

export const connectGooglePlugin = (plugin) => new Promise(async (resolve, reject) => {
  let popup;
  let closedTimer;
  let finished = false;
  const cleanup = () => {
    window.removeEventListener('message', onMessage);
    if (closedTimer) window.clearInterval(closedTimer);
  };
  const finish = (ok, message) => {
    if (finished) return;
    finished = true;
    cleanup();
    if (ok) resolve();
    else reject(new Error(message || 'Google login was cancelled'));
  };
  const onMessage = (event) => {
    const data = event?.data;
    if (!data || data.source !== 'workloop-google-oauth') return;
    finish(Boolean(data.ok), data.message);
  };
  window.addEventListener('message', onMessage);
  try {
    const authorizationUrl = await startGoogleOauth(plugin);
    popup = window.open(authorizationUrl, 'google-oauth', 'popup=yes,width=560,height=720');
    if (!popup) {
      finish(false, 'Pop-up was blocked. Allow pop-ups and try again.');
      return;
    }
  } catch (error) {
    finish(false, error.message);
    return;
  }
  closedTimer = window.setInterval(() => {
    if (popup && popup.closed) {
      finish(false, 'Google login window was closed.');
    }
  }, 500);
});

export const disconnectGooglePlugin = async (plugin) => {
  await axios.delete(`${googleApi()}/google/disconnect`, {
    headers: authHeaders(),
    params: { plugin },
  });
};

export const syncGoogleCalendar = async (onProgress) => {
  if (typeof onProgress !== 'function') {
    try {
      const response = await axios.post(
        `${googleApi()}/google/calendar/sync`,
        {},
        { headers: { ...authHeaders(), Accept: 'application/json', 'Content-Type': 'application/json' } }
      );
      return response.data || {};
    } catch (error) {
      throw new Error(errorMessage(error, 'Calendar sync failed'));
    }
  }
  const response = await fetch(`${googleApi()}/google/calendar/sync/progress`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      Accept: 'application/x-ndjson',
      'Content-Type': 'application/json',
    },
    body: '{}',
  });
  if (!response.ok) {
    let message = 'Calendar sync failed';
    try {
      const data = await response.json();
      message = data?.error || data?.message || message;
    } catch {
      // keep fallback
    }
    throw new Error(message);
  }
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Calendar sync failed');
  }
  const decoder = new TextDecoder();
  let buffer = '';
  let last = {};
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        last = JSON.parse(line);
      } catch {
        continue;
      }
      if (last.total != null) {
        onProgress(Number(last.done) || 0, Number(last.total) || 0, last.title || '');
      }
    }
  }
  if (buffer.trim()) {
    try {
      last = JSON.parse(buffer);
      if (last.total != null) {
        onProgress(Number(last.done) || 0, Number(last.total) || 0, last.title || '');
      }
    } catch {
      // ignore trailing junk
    }
  }
  if (last.error) {
    throw new Error(last.error);
  }
  return last;
};

export const uploadToGoogleDrive = async (file) => {
  const form = new FormData();
  form.append('file', file);
  try {
    const response = await axios.post(`${googleApi()}/google/drive/upload`, form, {
      headers: authHeaders(),
    });
    return response.data || {};
  } catch (error) {
    throw new Error(errorMessage(error, 'Drive upload failed'));
  }
};

const UUID_KEY_RE = /^[0-9a-f-]{36}(\.[a-z0-9._-]{1,32})?$/i;

const storedWorkloopFileKey = (attachment) => {
  const src = String(attachment?.fileUrl || '');
  if (!src || src.startsWith('blob:') || src.startsWith('data:') || src.startsWith('local-att-')) {
    return '';
  }
  const key = extractInternalFileKey(src);
  return UUID_KEY_RE.test(key) ? key : '';
};

export const shareStoredFileToGoogleDrive = async (fileKey, fileName, folderId) => {
  try {
    const response = await axios.post(
      `${googleApi()}/google/drive/share-stored`,
      { fileKey, fileName, folderId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    );
    return response.data || {};
  } catch (error) {
    throw new Error(errorMessage(error, 'Drive upload failed'));
  }
};

export const createGoogleDriveFolder = async (folderName) => {
  try {
    const response = await axios.post(
      `${googleApi()}/google/drive/share-stored`,
      { folderName },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    );
    return response.data || {};
  } catch (error) {
    throw new Error(errorMessage(error, 'Could not create the Drive folder'));
  }
};

export const shareStoredFilesToGoogleDriveFolder = async (folderName, files) => {
  try {
    const response = await axios.post(
      `${googleApi()}/google/drive/share-stored`,
      { folderName, files },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    );
    return response.data || {};
  } catch (error) {
    throw new Error(errorMessage(error, 'Drive upload failed'));
  }
};

const fileFromBlob = (blob, name, type) =>
  new File([blob], name || 'file', { type: type || blob?.type || 'application/octet-stream' });

export const fileFromChatAttachment = async (attachment) => {
  if (!attachment) return null;
  const name = attachment.fileName || 'file';
  if (attachment.file instanceof File) {
    return new File([attachment.file], name, { type: attachment.file.type || 'application/octet-stream' });
  }
  if (attachment.file instanceof Blob) {
    return fileFromBlob(attachment.file, name, attachment.file.type);
  }
  const localId = attachment.localFileId
    || (String(attachment.fileUrl || '').startsWith('local-att-') ? attachment.fileUrl : '');
  if (localId) {
    const { readLocalFile } = await import('./offlineAttachmentStore');
    const stored = await readLocalFile(localId);
    if (stored?.blob) return fileFromBlob(stored.blob, name, stored.blob.type);
  }
  const src = attachment.fileUrl;
  if (!src) return null;
  if (src.startsWith('blob:') || src.startsWith('data:')) {
    const response = await fetch(src);
    if (!response.ok) throw new Error(`Could not read ${name}`);
    return fileFromBlob(await response.blob(), name);
  }
  return null;
};

export const uploadAttachmentsToGoogleDrive = async (attachments = [], folderName, onProgress) => {
  const storedFiles = [];
  for (const attachment of attachments) {
    const storedKey = storedWorkloopFileKey(attachment);
    if (!storedKey) continue;
    storedFiles.push({
      fileKey: storedKey,
      fileName: attachment.fileName || 'file',
    });
  }
  if (folderName && storedFiles.length) {
    const folder = await createGoogleDriveFolder(folderName);
    if (!folder?.id || !folder?.shareUrl) {
      throw new Error('Could not get a Drive folder link.');
    }
    onProgress?.(0, storedFiles.length);
    let done = 0;
    for (const file of storedFiles) {
      await shareStoredFileToGoogleDrive(file.fileKey, file.fileName, folder.id);
      done += 1;
      onProgress?.(done, storedFiles.length);
    }
    return { ...folder, fileCount: done };
  }
  const results = [];
  const total = attachments.length;
  onProgress?.(0, total);
  for (const attachment of attachments) {
    const storedKey = storedWorkloopFileKey(attachment);
    let uploaded;
    if (storedKey) {
      uploaded = await shareStoredFileToGoogleDrive(storedKey, attachment.fileName);
    } else {
      const file = await fileFromChatAttachment(attachment);
      if (!file) continue;
      uploaded = await uploadToGoogleDrive(file);
    }
    results.push({
      id: uploaded.id,
      name: uploaded.name || attachment.fileName || 'File',
      shareUrl: uploaded.shareUrl,
    });
    onProgress?.(results.length, total);
  }
  if (!results.length) {
    throw new Error('Could not upload these files to Google Drive.');
  }
  return results;
};
